import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  LogOut,
  Loader2,
  LayoutGrid,
  List,
  Mail,
  MapPin,
  PackageCheck,
  Search,
  ShoppingBasket,
  UserCircle,
  X,
} from "lucide-react";
import { FaWhatsapp as WhatsApp } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { Footer, Header } from "./Home";
import ArticleSeo from "./articles/ArticleSeo";
import {
  formatNaira,
  homeTestCategories,
  homeTests,
  SITE_URL,
  WHATSAPP_NUMBER,
} from "./homeTestsData";

const pageUrl = `${SITE_URL}/home-tests`;
const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000"
).replace(/\/$/, "");

const HOME_TEST_CLIENT_ID_KEY = "zaviramedHomeTestClientId";
const HOME_TEST_CLIENT_NAME_KEY = "zaviramedHomeTestClientName";
const HOME_TEST_CLIENT_TOKEN_KEY = "zaviramedHomeTestClientToken";
const HOME_TEST_LAST_ORDER_KEY = "zaviramedHomeTestLastOrderId";
const HOME_TEST_CART_COOKIE_KEY = "zaviramed_home_test_cart";
const HOME_TEST_DELIVERY_COOKIE_KEY = "zaviramed_home_test_delivery_address";
const CART_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

function getCookieValue(name) {
  if (typeof document === "undefined") return "";

  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`))
      ?.split("=")
      .slice(1)
      .join("=") || ""
  );
}

function readCartCookie() {
  try {
    const value = getCookieValue(HOME_TEST_CART_COOKIE_KEY);
    if (!value) return [];

    const parsed = JSON.parse(decodeURIComponent(value));
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => ({
        productId: String(item.productId || item.id || ""),
        quantity: Math.min(Math.max(Number(item.quantity || 1), 1), 10),
      }))
      .filter((item) => item.productId);
  } catch {
    return [];
  }
}

function writeCartCookie(items = []) {
  if (typeof document === "undefined") return;

  const safeItems = items
    .map((item) => ({
      productId: String(item.productId || ""),
      quantity: Math.min(Math.max(Number(item.quantity || 1), 1), 10),
    }))
    .filter((item) => item.productId);

  document.cookie = `${HOME_TEST_CART_COOKIE_KEY}=${encodeURIComponent(
    JSON.stringify(safeItems)
  )}; max-age=${CART_COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function readDeliveryAddressCookie() {
  try {
    const value = getCookieValue(HOME_TEST_DELIVERY_COOKIE_KEY);
    if (!value) {
      return {
        fullAddress: "",
        city: "",
        state: "",
        landmark: "",
      };
    }

    const parsed = JSON.parse(decodeURIComponent(value));

    return {
      fullAddress: String(parsed.fullAddress || ""),
      city: String(parsed.city || ""),
      state: String(parsed.state || ""),
      landmark: String(parsed.landmark || ""),
    };
  } catch {
    return {
      fullAddress: "",
      city: "",
      state: "",
      landmark: "",
    };
  }
}

function writeDeliveryAddressCookie(address = {}) {
  if (typeof document === "undefined") return;

  const safeAddress = {
    fullAddress: String(address.fullAddress || ""),
    city: String(address.city || ""),
    state: String(address.state || ""),
    landmark: String(address.landmark || ""),
  };

  document.cookie = `${HOME_TEST_DELIVERY_COOKIE_KEY}=${encodeURIComponent(
    JSON.stringify(safeAddress)
  )}; max-age=${CART_COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function getSavedHomeTestClientId() {
  if (typeof window === "undefined") return "";

  return localStorage.getItem(HOME_TEST_CLIENT_TOKEN_KEY)
    ? localStorage.getItem(HOME_TEST_CLIENT_ID_KEY) || ""
    : "";
}

function getSavedHomeTestClientToken() {
  if (typeof window === "undefined") return "";

  return localStorage.getItem(HOME_TEST_CLIENT_TOKEN_KEY) || "";
}

function buildCartProducts(cartItems = []) {
  return cartItems
    .map((item) => {
      const product = homeTests.find((test) => test.id === item.productId);
      if (!product) return null;

      return {
        ...product,
        quantity: Math.min(Math.max(Number(item.quantity || 1), 1), 10),
      };
    })
    .filter(Boolean);
}

function buildCartSummary(cartProducts = []) {
  const total = cartProducts.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
    0
  );
  const count = cartProducts.reduce(
    (sum, item) => sum + Number(item.quantity || 1),
    0
  );

  return {
    id: "home-test-cart",
    name: `${count || 0} home test item${count === 1 ? "" : "s"}`,
    price: total,
    description: cartProducts
      .map((item) =>
        item.quantity > 1 ? `${item.name} x${item.quantity}` : item.name
      )
      .join(", "),
  };
}

function saveHomeTestAccount(payload = {}) {
  if (payload.clientId) {
    localStorage.setItem(HOME_TEST_CLIENT_ID_KEY, payload.clientId);
  }

  if (payload.clientName) {
    localStorage.setItem(HOME_TEST_CLIENT_NAME_KEY, payload.clientName);
  }

  const clientToken = payload.clientToken || payload.homeTestToken;

  if (clientToken) {
    localStorage.setItem(HOME_TEST_CLIENT_TOKEN_KEY, clientToken);
  }

  if (payload.orderId) {
    localStorage.setItem(HOME_TEST_LAST_ORDER_KEY, payload.orderId);
  }

  window.dispatchEvent(new Event("zaviramed-home-test-account"));
}

function clearHomeTestAccount() {
  localStorage.removeItem(HOME_TEST_CLIENT_ID_KEY);
  localStorage.removeItem(HOME_TEST_CLIENT_NAME_KEY);
  localStorage.removeItem(HOME_TEST_CLIENT_TOKEN_KEY);
  localStorage.removeItem(HOME_TEST_LAST_ORDER_KEY);
  window.dispatchEvent(new Event("zaviramed-home-test-account"));
}

export function GuestAccountMenu({
  cartCount = 0,
  onCartClick,
  onOpenAuth,
} = {}) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState(() => ({
    clientId: getSavedHomeTestClientId(),
    clientName: getSavedHomeTestClientId()
      ? localStorage.getItem(HOME_TEST_CLIENT_NAME_KEY) || ""
      : "",
  }));

  const handleLogout = () => {
    clearHomeTestAccount();
    setAccount({
      clientId: "",
      clientName: "",
    });
    setOpen(false);
  };

  useEffect(() => {
    const syncAccount = () => {
      const clientId = getSavedHomeTestClientId();

      setAccount({
        clientId,
        clientName: clientId
          ? localStorage.getItem(HOME_TEST_CLIENT_NAME_KEY) || ""
          : "",
      });
    };

    window.addEventListener("storage", syncAccount);
    window.addEventListener("zaviramed-home-test-account", syncAccount);

    return () => {
      window.removeEventListener("storage", syncAccount);
      window.removeEventListener("zaviramed-home-test-account", syncAccount);
    };
  }, []);

  if (!account.clientId && !onCartClick && !onOpenAuth) return null;

  return (
    <div className="fixed right-4 top-24 z-40 flex items-center gap-2 sm:right-6">
      {onCartClick && (
        <button
          type="button"
          onClick={onCartClick}
          className="relative grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-[#075E54] shadow-sm transition hover:bg-slate-50 active:scale-[0.98]"
          aria-label="Open cart"
        >
          <ShoppingBasket size={20} />
          {cartCount > 0 && (
            <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#25D366] px-1 text-[10px] font-black text-[#075E54]">
              {cartCount > 99 ? "99+" : cartCount}
            </span>
          )}
        </button>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-[0.98]"
          aria-expanded={open}
        >
          <UserCircle size={20} className="text-[#075E54]" />
          <span className="hidden max-w-[140px] truncate sm:block">
            {account.clientName || "Account"}
          </span>
          <ChevronDown size={14} />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
            {account.clientId ? (
              <>
                <Link
                  to="/orders"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 hover:text-[#075E54]"
                >
                  <PackageCheck size={17} />
                  Orders
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-bold text-red-600 transition hover:bg-red-50"
                >
                  <LogOut size={17} />
                  Log out
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpenAuth?.("login");
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-bold text-slate-700 transition hover:bg-slate-50 hover:text-[#075E54]"
                >
                  <UserCircle size={17} />
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpenAuth?.("new");
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-bold text-slate-700 transition hover:bg-slate-50 hover:text-[#075E54]"
                >
                  <PackageCheck size={17} />
                  Sign up
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchPanel({ value, onChange }) {
  return (
    <label className="zv-kit-search">
      <span className="sr-only">Search home testing kits</span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search"
        aria-label="Search home testing kits"
      />
      <Search aria-hidden="true" size={22} strokeWidth={1.8} />
    </label>
  );
}

function CategoryTabs({ value, onChange }) {
  return (
    <div
      className="
        overflow-x-auto
        [scrollbar-width:none]
        [&::-webkit-scrollbar]:hidden
      "
    >
      <div className="flex min-w-max items-center gap-2">
        {homeTestCategories.map((item) => {
          const active = value === item;

          return (
            <button
              key={item}
              type="button"
              onClick={() => onChange(item)}
              className={`
                rounded-xl
                border
                px-4 py-2.5
                text-[11px] font-semibold
                transition
                sm:px-5 sm:text-xs

                ${
                  active
                    ? `
                      border-[#075E54]
                      bg-[#075E54]
                      text-white
                      shadow-sm
                    `
                    : `
                      border-slate-200
                      bg-white
                      text-slate-500
                      hover:border-[#075E54]/25
                      hover:bg-emerald-50
                      hover:text-[#075E54]
                    `
                }
              `}
            >
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 placeholder:text-slate-400";

function PurchaseModal({
  test,
  cartItems = [],
  deliveryAddress = {
    fullAddress: "",
    city: "",
    state: "",
    landmark: "",
  },
  initialAccountMode = "new",
  loggedInClientId = "",
  onClose,
  onSuccess,
}) {
  const navigate = useNavigate();
  const autoCheckoutKeyRef = useRef("");
  const [accountMode, setAccountMode] = useState(initialAccountMode);
  const [form, setForm] = useState({
    name: "",
    email: "",
    age: "",
    sex: "",
    whatsappNumber: "",
  });
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [otpState, setOtpState] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    autoCheckoutKeyRef.current = "";
    setAccountMode(initialAccountMode);
    setOtpState(null);
    setResult(null);
    setError("");
  }, [initialAccountMode, test?.id]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openPayment = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSuccess = useCallback((payload) => {
    setResult(payload);
    setOtpState(null);
    setError("");
    saveHomeTestAccount(payload);
    onSuccess?.(payload);
  }, [onSuccess]);

  const startPurchase = useCallback(async () => {
    if (!test) return;

    setLoading(true);
    setError("");

    try {
      const checkoutItems = cartItems.length
        ? cartItems
        : [{ productId: test.id, quantity: 1 }];
      const payload =
        accountMode === "logged_in"
          ? {
              productId: test.id,
              cartItems: checkoutItems,
              accountMode,
              clientId: loggedInClientId,
              clientToken: getSavedHomeTestClientToken(),
              deliveryAddress,
            }
          : accountMode === "existing"
          ? {
              productId: test.id,
              cartItems: checkoutItems,
              accountMode,
              identifier,
              deliveryAddress,
            }
          : {
              productId: test.id,
              cartItems: checkoutItems,
              accountMode,
              deliveryAddress,
              ...form,
            };

      const response = await axios.post(
        `${BACKEND_URL}/home-tests/purchase/start`,
        payload
      );

      if (response.data?.requiresOtp) {
        setOtpState({
          clientId: response.data.clientId,
          maskedEmail: response.data.maskedEmail,
        });
        setOtp("");
        return;
      }

      handleSuccess(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We could not start this purchase. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    accountMode,
    cartItems,
    deliveryAddress,
    form,
    handleSuccess,
    identifier,
    loggedInClientId,
    test,
  ]);

  useEffect(() => {
    if (
      !test ||
      accountMode !== "logged_in" ||
      !loggedInClientId ||
      !deliveryAddress.fullAddress.trim() ||
      result ||
      otpState ||
      loading
    ) {
      return;
    }

    const checkoutItems = cartItems.length
      ? cartItems
      : [{ productId: test.id, quantity: 1 }];
    const checkoutKey = [
      test.id,
      loggedInClientId,
      deliveryAddress.fullAddress,
      checkoutItems.map((item) => `${item.productId}:${item.quantity}`).join("|"),
    ].join(":");

    if (autoCheckoutKeyRef.current === checkoutKey) return;

    autoCheckoutKeyRef.current = checkoutKey;
    startPurchase();
  }, [
    accountMode,
    cartItems,
    deliveryAddress.fullAddress,
    loading,
    loggedInClientId,
    otpState,
    result,
    startPurchase,
    test,
  ]);

  if (!test) return null;

  const verifyOtp = async () => {
    if (!otpState?.clientId) return;

    setLoading(true);
    setError("");

    try {
      const checkoutItems = cartItems.length
        ? cartItems
        : [{ productId: test.id, quantity: 1 }];
      const response = await axios.post(
        `${BACKEND_URL}/home-tests/purchase/verify-otp`,
        {
          clientId: otpState.clientId,
          productId: test.id,
          cartItems: checkoutItems,
          otp,
          deliveryAddress,
        }
      );

      handleSuccess(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We could not verify this code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const canContinueToCheckout =
    accountMode === "existing"
      ? identifier.trim().length > 2
      : form.name.trim() &&
        form.email.trim() &&
        form.age &&
        form.sex &&
        form.whatsappNumber.trim();
  const canSubmit = canContinueToCheckout && deliveryAddress.fullAddress.trim();

  return (
    <div className="fixed inset-0 z-[90] flex items-end bg-slate-900/50 px-3 pb-3 sm:items-center sm:justify-center sm:p-6">
      <div className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 active:scale-[0.97]"
          aria-label="Close purchase form"
        >
          <X size={17} />
        </button>

        {/* <div className="pr-10">
          <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] text-[#075E54]">
            {test.name}
          </h2>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            {formatNaira(test.price)} · Pay first, then continue on WhatsApp.
          </p>
        </div> */}

        {result ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 text-[#25D366]" size={22} />
                <div>
                  <p className="text-sm font-black text-[#075E54]">
                    Request created
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {result.message}
                  </p>
                  {result.emailSent === false && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">
                      Email did not send, but your payment link is ready below.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => openPayment(result.paymentLink)}
                className="w-full rounded-xl bg-[#075E54] px-4 py-3 text-sm font-black text-white transition hover:bg-[#064f47] active:scale-[0.98]"
              >
                Pay now
              </button>
              {(result.trackingKey || result.orderId) && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/orders/${result.trackingKey || result.orderId}`)
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 active:scale-[0.98]"
                >
                  Track order
                </button>
              )}
            </div>
          </div>
        ) : otpState ? (
          <div className="mt-10 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 text-[#075E54]" size={20} />
                <div>
                  <p className="text-sm font-black text-slate-900">
                    Enter the code sent to {otpState.maskedEmail}
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                    We use this to confirm the account before creating the
                    payment link.
                  </p>
                </div>
              </div>
            </div>

            <input
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              className={inputClass}
              inputMode="numeric"
              placeholder="6-digit code"
            />

            {error && (
              <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setOtpState(null)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-600 transition hover:bg-slate-50 active:scale-[0.98]"
              >
                Change details
              </button>
              <button
                type="button"
                onClick={verifyOtp}
                disabled={loading || otp.trim().length < 4}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#075E54] px-4 py-3 text-sm font-black text-white transition hover:bg-[#064f47] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {loading && <Loader2 className="animate-spin" size={16} />}
                Continue
              </button>
            </div>
          </div>
        ) : accountMode === "logged_in" ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-center">
              <Loader2
                className="mx-auto animate-spin text-[#075E54]"
                size={26}
              />
              <p className="mt-3 text-sm font-black text-[#075E54]">
                Preparing your checkout
              </p>
              {/* <p className="mx-auto mt-1 max-w-sm text-xs font-semibold leading-5 text-slate-600">
                We found your Zaviramed account, so there is no need to login
                again.
              </p> */}
            </div>

            {error && (
              <div className="space-y-3">
                <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700">
                  {error}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const checkoutItems = cartItems.length
                      ? cartItems
                      : [{ productId: test.id, quantity: 1 }];
                    autoCheckoutKeyRef.current = [
                      test.id,
                      loggedInClientId,
                      deliveryAddress.fullAddress,
                      checkoutItems
                        .map((item) => `${item.productId}:${item.quantity}`)
                        .join("|"),
                    ].join(":");
                    startPurchase();
                  }}
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#075E54] px-5 py-3 text-sm font-black text-white transition hover:bg-[#064f47] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {loading && <Loader2 className="animate-spin" size={16} />}
                  Try again
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-[#075E54]">
                Have you texted Zaviramed on WhatsApp before?
              </p>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">
                Use that WhatsApp number to login. 
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1">
              <button
                type="button"
                onClick={() => setAccountMode("new")}
                className={`rounded-xl px-3 py-2.5 text-xs font-black transition ${
                  accountMode === "new"
                    ? "bg-[#075E54] text-white"
                    : "text-slate-500 hover:bg-white"
                }`}
              >
                New customer
              </button>
              <button
                type="button"
                onClick={() => setAccountMode("existing")}
                className={`rounded-xl px-3 py-2.5 text-xs font-black transition ${
                  accountMode === "existing"
                    ? "bg-[#075E54] text-white"
                    : "text-slate-500 hover:bg-white"
                }`}
              >
                Existing customer
              </button>
            </div>

            {accountMode === "new" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  className={`${inputClass} sm:col-span-2`}
                  placeholder="Full name"
                />
                <input
                  value={form.email}
                  onChange={(event) => updateForm("email", event.target.value)}
                  className={`${inputClass} sm:col-span-2`}
                  type="email"
                  placeholder="Email for payment"
                />
                <input
                  value={form.age}
                  onChange={(event) => updateForm("age", event.target.value)}
                  className={inputClass}
                  type="number"
                  min="1"
                  placeholder="Age"
                />
                <select
                  value={form.sex}
                  onChange={(event) => updateForm("sex", event.target.value)}
                  className={inputClass}
                >
                  <option value="">Sex</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
                <input
                  value={form.whatsappNumber}
                  onChange={(event) =>
                    updateForm("whatsappNumber", event.target.value)
                  }
                  className={`${inputClass} sm:col-span-2`}
                  inputMode="tel"
                  placeholder="WhatsApp number"
                />
              </div>
            ) : (
              <div>
                <input
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className={inputClass}
                  placeholder="Email or WhatsApp number"
                />
                <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                  We will send an OTP to your email account.
                </p>
              </div>
            )}

            {error && (
              <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={startPurchase}
              disabled={loading || !canSubmit}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#075E54] px-5 py-3.5 text-sm font-black text-white transition hover:bg-[#064f47] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {accountMode === "existing" ? "Send OTP" : "Create payment link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TestCard({ test, onPurchase }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <article className="zv-kit-card">
      <div className="zv-kit-card-media">
        <img src={test.image} alt={test.name + " home testing kit packaging"} loading="lazy" />
      </div>
      <div className="zv-kit-card-content">
        <div>
          <h2>{test.name}</h2>
          <p>{test.description}</p>
          <button
            className="zv-kit-detail-toggle"
            type="button"
            aria-expanded={detailsOpen}
            aria-label={detailsOpen ? `Hide details for ${test.name}` : `Show details for ${test.name}`}
            onClick={() => setDetailsOpen((current) => !current)}
          >
            {detailsOpen ? <ChevronDown aria-hidden="true" size={20} /> : <ArrowRight aria-hidden="true" size={20} />}
          </button>
          {detailsOpen && (
            <ul className="zv-kit-includes">
              {(test.includes || []).map((detail) => <li key={detail}>{detail}</li>)}
            </ul>
          )}
        </div>
        <div className="zv-kit-card-bottom">
          <strong>{formatNaira(test.price)}</strong>
          <button type="button" onClick={() => onPurchase(test)} className="zv-kit-add">
            <ShoppingBasket size={19} aria-hidden="true" />
            Add to Cart
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div
      className="
        col-span-full
        rounded-2xl
        border border-slate-200
        bg-white
        px-6 py-16
        text-center
        shadow-sm
      "
    >
      <p className="text-base font-black text-[#075E54]">No home tests found</p>

      <p className="mx-auto mt-2 max-w-md text-xs font-medium leading-5 text-slate-500">
        Try another search or select another category.
      </p>
    </div>
  );
}

function AccountLoginModal({ open, onClose }) {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [otpState, setOtpState] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const startLogin = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.post(
        `${BACKEND_URL}/home-tests/account/login/start`,
        { identifier }
      );

      setOtpState({
        clientId: response.data?.clientId,
        maskedEmail: response.data?.maskedEmail,
      });
      setOtp("");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We could not send your code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyLogin = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.post(
        `${BACKEND_URL}/home-tests/account/login/verify`,
        {
          clientId: otpState?.clientId,
          otp,
        }
      );

      saveHomeTestAccount(response.data);
      onClose();
      navigate("/orders");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We could not verify your code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end bg-slate-950/45 px-3 pb-3 sm:items-center sm:justify-center sm:p-6">
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
          aria-label="Close login"
        >
          <X size={17} />
        </button>

        <div className="pr-10">
          {/* <p className="text-xs font-black uppercase tracking-[0.18em] text-[#25D366]">
            Zaviramed account
          </p> */}
          <h2 className=" text-2xl font-black tracking-[-0.04em] text-[#075E54]">
            Login
          </h2>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
            If you have texted us on WhatsApp before, use that same WhatsApp
            number. We will send an OTP to the email on your account.
          </p>
        </div>

        {otpState ? (
          <div className="mt-6 space-y-4 ">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm font-black text-[#075E54]">
                Code sent to {otpState.maskedEmail}
              </p>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">
                Enter the code to open your orders.
              </p>
            </div>

            <input
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              className={inputClass}
              inputMode="numeric"
              placeholder="6-digit code"
            />

            {error && (
              <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setOtpState(null)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-600 transition hover:bg-slate-50"
              >
                Change number
              </button>
              <button
                type="button"
                onClick={verifyLogin}
                disabled={loading || otp.trim().length < 4}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#075E54] px-4 py-3 text-sm font-black text-white transition hover:bg-[#064f47] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {loading && <Loader2 className="animate-spin" size={16} />}
                Open orders
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <input
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              className={inputClass}
              placeholder="Email or WhatsApp number"
            />

            {error && (
              <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={startLogin}
              disabled={loading || identifier.trim().length < 3}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#075E54] px-4 py-3 text-sm font-black text-white transition hover:bg-[#064f47] disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              Send OTP
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CartModal({
  open,
  cartProducts,
  deliveryAddress,
  onDeliveryAddressChange,
  onClose,
  onCheckout,
  onUpdateQuantity,
  onRemove,
  onClear,
}) {
  const [addressOpen, setAddressOpen] = useState(false);
  const hasDeliveryAddress = Boolean(deliveryAddress?.fullAddress?.trim());

  if (!open) return null;

  const total = cartProducts.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
    0
  );

  const updateDeliveryAddress = (key, value) => {
    onDeliveryAddressChange?.({
      ...(deliveryAddress || {}),
      [key]: value,
    });
  };

  const handleCheckout = () => {
    if (!hasDeliveryAddress) {
      setAddressOpen(true);
      return;
    }

    onCheckout("existing");
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end bg-slate-950/45 px-3 pb-3 sm:items-center sm:justify-center sm:p-6">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            {/* <p className="text-xs font-black uppercase tracking-[0.18em] text-[#25D366]">
              Home tests
            </p> */}
            <h2 className="text-2xl font-semibold tracking-[-0.01em] text-[#075E54]">
              Your cart
            </h2>
            {/* <p className="mt-1 text-sm font-semibold text-slate-500">
              Your cart is saved on this device until you checkout or remove
              the items.
            </p> */}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label="Close cart"
          >
            <X size={17} />
          </button>
        </div>

        {cartProducts.length ? (
          <div className="mt-6 space-y-3">
            {cartProducts.map((item) => (
              <div
                key={item.id}
                className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[88px_1fr_auto]"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-20 w-20 rounded-xl object-cover"
                  loading="lazy"
                />

                <div>
                  <h3 className="text-sm font-black text-slate-950">
                    {item.name}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs font-medium leading-5 text-slate-500">
                    {item.description}
                  </p>
                  <p className="mt-2 text-sm font-black text-[#075E54]">
                    {formatNaira(item.price)}
                  </p>
                </div>

                <div className="flex items-center gap-2 sm:flex-col sm:items-end sm:justify-between">
                  <div className="inline-flex items-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <button
                      type="button"
                      onClick={() =>
                        onUpdateQuantity(item.id, item.quantity - 1)
                      }
                      className="grid h-9 w-9 place-items-center text-sm font-black text-slate-600 hover:bg-slate-50"
                      aria-label={`Reduce ${item.name}`}
                    >
                      -
                    </button>
                    <span className="grid h-9 min-w-10 place-items-center border-x border-slate-200 px-2 text-sm font-black text-slate-900">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onUpdateQuantity(item.id, item.quantity + 1)
                      }
                      className="grid h-9 w-9 place-items-center text-sm font-black text-slate-600 hover:bg-slate-50"
                      aria-label={`Increase ${item.name}`}
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    className="rounded-xl px-3 py-2 text-xs font-black text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <div className="relative mt-5 overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-br  p-5 ">
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full  blur-2xl" />

              <div className="relative flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Order total
                  </p>
                  <p className="mt-1 text-xs font-medium text-slate-400">
                    Delivery included
                  </p>
                </div>

                <p className="text-2xl font-black tracking-[-0.045em] text-[#075E54]">
                  {formatNaira(total)}
                </p>
              </div>

              <div className="relative my-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

            
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setAddressOpen((value) => !value)}
                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                aria-expanded={addressOpen}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="text-[#075E54]" size={18} />
                  <span>
                    <span className="block text-sm font-black text-slate-950">
                      Delivery address
                    </span>
                    <span
                      className={`mt-1 block text-xs font-semibold ${
                        hasDeliveryAddress ? "text-[#075E54]" : "text-amber-700"
                      }`}
                    >
                      {hasDeliveryAddress
                        ? "Address added"
                        : "Required before checkout"}
                    </span>
                  </span>
                </span>
                <ChevronDown
                  size={17}
                  className={`shrink-0 text-slate-400 transition ${
                    addressOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {addressOpen && (
                <div className="border-t border-slate-100 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <textarea
                      value={deliveryAddress?.fullAddress || ""}
                      onChange={(event) =>
                        updateDeliveryAddress("fullAddress", event.target.value)
                      }
                      className={`${inputClass} min-h-[96px] resize-none sm:col-span-2`}
                      placeholder="Full delivery address"
                    />
                    <input
                      value={deliveryAddress?.city || ""}
                      onChange={(event) =>
                        updateDeliveryAddress("city", event.target.value)
                      }
                      className={inputClass}
                      placeholder="City"
                    />
                    <input
                      value={deliveryAddress?.state || ""}
                      onChange={(event) =>
                        updateDeliveryAddress("state", event.target.value)
                      }
                      className={inputClass}
                      placeholder="State"
                    />
                    <input
                      value={deliveryAddress?.landmark || ""}
                      onChange={(event) =>
                        updateDeliveryAddress("landmark", event.target.value)
                      }
                      className={`${inputClass} sm:col-span-2`}
                      placeholder="Nearby landmark"
                    />
                  </div>

               
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <button
                type="button"
                onClick={handleCheckout}
                className="rounded-xl bg-[#075E54] px-5 py-3.5 text-sm font-black text-white transition hover:bg-[#064f47] active:scale-[0.98]"
              >
                {hasDeliveryAddress ? "Checkout" : "Add address to checkout"}
              </button>
              <button
                type="button"
                onClick={onClear}
                className="rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-black text-slate-600 transition hover:bg-slate-50 active:scale-[0.98]"
              >
                Clear cart
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
            <PackageCheck className="mx-auto text-[#075E54]" size={32} />
            <h3 className="mt-3 text-base font-black text-slate-950">
              Your cart is empty
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
              Add a Rain or Sabi testing kit before checkout.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HomeTests() {
  const [category, setCategory] = useState("All Tests");
  const [viewMode, setViewMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTest, setSelectedTest] = useState(null);
  const [checkoutMode, setCheckoutMode] = useState("existing");
  const [cartOpen, setCartOpen] = useState(false);
  const [accountLoginOpen, setAccountLoginOpen] = useState(false);
  const [cartItems, setCartItems] = useState(() => readCartCookie());
  const [deliveryAddress, setDeliveryAddress] = useState(() =>
    readDeliveryAddressCookie()
  );
  const [loggedInClientId, setLoggedInClientId] = useState(() =>
    getSavedHomeTestClientId()
  );

  useEffect(() => {
    writeCartCookie(cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeDeliveryAddressCookie(deliveryAddress);
  }, [deliveryAddress]);

  useEffect(() => {
    const syncAccount = () => {
      setLoggedInClientId(getSavedHomeTestClientId());
    };

    window.addEventListener("storage", syncAccount);
    window.addEventListener("zaviramed-home-test-account", syncAccount);

    return () => {
      window.removeEventListener("storage", syncAccount);
      window.removeEventListener("zaviramed-home-test-account", syncAccount);
    };
  }, []);

  const cartProducts = useMemo(() => buildCartProducts(cartItems), [cartItems]);
  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) =>
          sum + Math.min(Math.max(Number(item.quantity || 1), 1), 10),
        0
      ),
    [cartItems]
  );
  const cartSummary = useMemo(
    () => buildCartSummary(cartProducts),
    [cartProducts]
  );

  const filteredTests = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return homeTests.filter((test) => {
      if (test.available === false) return false;

      const matchesCategory =
        category === "All Tests" || test.category === category;

      const searchableText = [
        test.name,
        test.category,
        test.description,
        ...(test.includes || []),
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      return test.type === "package" && matchesCategory && matchesSearch;
    });
  }, [category, searchTerm]);

  const addToCart = (test) => {
    setCartItems((current) => {
      const existing = current.find((item) => item.productId === test.id);

      if (existing) {
        return current.map((item) =>
          item.productId === test.id
            ? {
                ...item,
                quantity: Math.min(Number(item.quantity || 1) + 1, 10),
              }
            : item
        );
      }

      return [...current, { productId: test.id, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const updateCartQuantity = (productId, quantity) => {
    const nextQuantity = Number(quantity || 0);

    setCartItems((current) => {
      if (nextQuantity <= 0) {
        return current.filter((item) => item.productId !== productId);
      }

      return current.map((item) =>
        item.productId === productId
          ? { ...item, quantity: Math.min(nextQuantity, 10) }
          : item
      );
    });
  };

  const removeCartItem = (productId) => {
    setCartItems((current) =>
      current.filter((item) => item.productId !== productId)
    );
  };

  const openCheckout = (accountMode = "existing") => {
    if (!cartProducts.length) {
      setCartOpen(true);
      return;
    }

    if (!deliveryAddress.fullAddress.trim()) {
      setCartOpen(true);
      return;
    }

    const savedClientId = getSavedHomeTestClientId();
    setLoggedInClientId(savedClientId);
    setCheckoutMode(savedClientId ? "logged_in" : accountMode);
    setSelectedTest(cartSummary);
    setCartOpen(false);
  };

  const handleAccountAction = (action) => {
    if (action === "login") {
      setAccountLoginOpen(true);
      return;
    }

    openCheckout("new");
  };

  const checkoutItems = useMemo(
    () =>
      cartProducts.map((item) => ({
        productId: item.id,
        quantity: item.quantity,
      })),
    [cartProducts]
  );

  return (
    <main
      className="
        zv-site zv-shop relative min-h-screen
        bg-[#f8faf9]
        text-slate-950
      "
    >
      <ArticleSeo
        title="Home Tests and Home Testing Kits in Nigeria | Zaviramed"
        description="Order Zaviramed Rain and Sabi home testing kits with private WhatsApp guidance after purchase."
        canonical={pageUrl}
        image={`${SITE_URL}/design/kits-rain.webp`}
        type="website"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Zaviramed Home Tests",
            description:
              "Zaviramed Rain and Sabi private home testing kits.",
            url: pageUrl,
          },
        ]}
      />

      <div className="relative z-30">
        <Header heroAligned />
      </div>
      <GuestAccountMenu
        cartCount={cartCount}
        onCartClick={() => setCartOpen(true)}
        onOpenAuth={handleAccountAction}
      />

      <div id="main-content" className="zv-kit-page">
        <section className="zv-kit-hero-shell" aria-labelledby="zv-kit-heading">
          <div className="zv-kit-hero" style={{ backgroundImage: 'url("/design/kits-hero.webp?v=hires-20261029")' }}>
            <div className="zv-kit-hero-copy">
              <h1 id="zv-kit-heading">Order Zaviramed home testing kit</h1>
              <p>Private testing kits with Zaviramed WhatsApp guidance after purchase.</p>
              <SearchPanel value={searchTerm} onChange={setSearchTerm} />
            </div>
          </div>
        </section>
        <section className="zv-kit-catalog" aria-label="Available home testing kits">
          <div className="zv-kit-catalog-inner">
            <div className="zv-kit-filter-wrap">
              <div className="zv-kit-category">
                <CategoryTabs value={category} onChange={setCategory} />
              </div>
              <div className="zv-kit-view-controls" role="group" aria-label="Product layout">
                <button
                  type="button"
                  className="zv-kit-view-button"
                  aria-label="List view"
                  title="List view"
                  aria-pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                >
                  <List size={20} strokeWidth={2} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="zv-kit-view-button"
                  aria-label="Grid view"
                  title="Grid view"
                  aria-pressed={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                >
                  <LayoutGrid size={20} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
            </div>
            <div id="tests" className={`zv-kit-product-list${viewMode === "grid" ? " is-grid" : ""}`}>
              {filteredTests.length ? (
                filteredTests.map((test) => (
                  <TestCard key={test.id} test={test} onPurchase={addToCart} />
                ))
              ) : (
                <EmptyState />
              )}
            </div>
          </div>
        </section>
      </div>

      {/* WHATSAPP */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
          "Hello Zaviramed, I need help choosing a home test."
        )}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat with Zaviramed on WhatsApp"
        className="
          fixed bottom-5 right-5 z-40

          grid h-14 w-14
          place-items-center

          rounded-full

          border border-[#25D366]
          bg-[#25D366]

          text-white

          shadow-lg

          transition

          hover:bg-[#20bd5b]

          active:scale-[0.96]

          sm:h-[60px]
          sm:w-[60px]
        "
      >
        <WhatsApp size={27} />
      </a>

      <div className="relative z-10">
        <Footer />
      </div>

      <PurchaseModal
        test={selectedTest}
        cartItems={checkoutItems}
        deliveryAddress={deliveryAddress}
        initialAccountMode={checkoutMode}
        loggedInClientId={loggedInClientId}
        onClose={() => setSelectedTest(null)}
        onSuccess={() => setCartItems([])}
      />
      <CartModal
        open={cartOpen}
        cartProducts={cartProducts}
        deliveryAddress={deliveryAddress}
        onDeliveryAddressChange={setDeliveryAddress}
        onClose={() => setCartOpen(false)}
        onCheckout={openCheckout}
        onUpdateQuantity={updateCartQuantity}
        onRemove={removeCartItem}
        onClear={() => setCartItems([])}
      />
      <AccountLoginModal
        open={accountLoginOpen}
        onClose={() => setAccountLoginOpen(false)}
      />
    </main>
  );
}

